import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import SmoothScroll from "@/components/SmoothScroll";
import { AuthProvider } from "@/context/AuthContext";
import { QuoteProvider } from "@/context/QuoteContext";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import BulkOrders from "@/pages/BulkOrders";
import Wholesale from "@/pages/Wholesale";
import Distributor from "@/pages/Distributor";
import Quote from "@/pages/Quote";
import Sample from "@/pages/Sample";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import { PrivacyPolicy, CookiePolicy, Terms, RefundShipping } from "@/pages/Policies";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout, { ProtectedRoute } from "@/pages/admin/AdminLayout";
import Overview from "@/pages/admin/Overview";
import LeadsAdmin from "@/pages/admin/LeadsAdmin";
import BlogAdmin from "@/pages/admin/BlogAdmin";
import ProductsAdmin from "@/pages/admin/ProductsAdmin";
import TestimonialsAdmin from "@/pages/admin/TestimonialsAdmin";

function CookieConsentBanner() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return !localStorage.getItem("leafalchemy_cookie_consent");
  });
  const [manageOpen, setManageOpen] = useState(false);
  const [preferences, setPreferences] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const saved = localStorage.getItem("leafalchemy_cookie_consent");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences({ analytics: !!parsed.analytics, marketing: !!parsed.marketing });
      } catch (error) {
        // Ignore malformed saved preference.
      }
    }
  }, []);

  const save = (choice) => {
    const payload = {
      choice,
      analytics: Boolean(choice === "all" || preferences.analytics),
      marketing: Boolean(choice === "all" || preferences.marketing),
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem("leafalchemy_cookie_consent", JSON.stringify(payload));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 bg-forest text-cream shadow-[0_-8px_32px_rgba(15,81,50,0.18)] border-t border-cream/15" role="dialog" aria-live="polite" aria-label="Cookie preferences">
      <div className="max-w-6xl mx-auto px-5 py-4 lg:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <p className="font-medium">Cookie preferences</p>
            <p className="text-sm text-cream/75">
              We currently use only essential local storage for admin authentication and the site preference itself. No third-party analytics or tracking cookies are active.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => save("all")} className="rounded-full bg-gold text-ink hover:bg-cream">Accept All</Button>
            <Button type="button" variant="outline" onClick={() => save("essential")} className="rounded-full border-cream/30 text-cream bg-transparent hover:bg-cream/10">Reject Non-Essential</Button>
            <Button type="button" variant="outline" onClick={() => setManageOpen((v) => !v)} className="rounded-full border-cream/30 text-cream bg-transparent hover:bg-cream/10">Manage Preferences</Button>
          </div>
        </div>
        {manageOpen && (
          <div className="mt-4 rounded-xl border border-cream/15 bg-cream/5 p-4 text-sm">
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={preferences.analytics} onChange={(e) => setPreferences((prev) => ({ ...prev, analytics: e.target.checked }))} className="h-4 w-4 accent-gold" />
                <span>Analytics and optional tracking</span>
              </label>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={preferences.marketing} onChange={(e) => setPreferences((prev) => ({ ...prev, marketing: e.target.checked }))} className="h-4 w-4 accent-gold" />
                <span>Marketing and promotional communications</span>
              </label>
              <div className="flex gap-2 pt-2">
                <Button type="button" onClick={() => save("all")} className="rounded-full bg-gold text-ink hover:bg-cream">Save Preferences</Button>
                <Button type="button" variant="outline" onClick={() => save("essential")} className="rounded-full border-cream/30 text-cream bg-transparent hover:bg-cream/10">Use Essential Only</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <QuoteProvider>
            <SmoothScroll>
              <CookieConsentBanner />
              <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:slug" element={<ProductDetail />} />
                  <Route path="/bulk-orders" element={<BulkOrders />} />
                  <Route path="/wholesale-pricing" element={<Wholesale />} />
                  <Route path="/become-distributor" element={<Distributor />} />
                  <Route path="/request-quote" element={<Quote />} />
                  <Route path="/free-sample" element={<Sample />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/refund-shipping" element={<RefundShipping />} />
                </Route>

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                  <Route index element={<Overview />} />
                  <Route path="leads" element={<LeadsAdmin />} />
                  <Route path="blog" element={<BlogAdmin />} />
                  <Route path="products" element={<ProductsAdmin />} />
                  <Route path="testimonials" element={<TestimonialsAdmin />} />
                </Route>
              </Routes>
            </SmoothScroll>
          </QuoteProvider>
        </AuthProvider>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
